import type { Label } from './presets';

/**
 * Sound effects synthesised from code: whoosh, hit, boom, tick, click, riser, bubble pop,
 * typewriter, glitch and shimmer. The same functions make the files shipped with the app
 * (`apps/web/public/sfx`, regenerated with `pnpm generate:sfx`) and the
 * copies the render server makes on demand, so no sound library or licence is needed.
 */

export const SFX_RATE = 32_000;

export const SFX_KINDS = [
  'whoosh',
  'hit',
  'boom',
  'tick',
  'click',
  'riser',
  'pop',
  'typewriter',
  'glitch',
  'shimmer',
] as const;
export type SfxKind = (typeof SFX_KINDS)[number];

const l = (en: string, uk: string, pl: string, ro: string, es: string, ru: string): Label => [
  en,
  uk,
  pl,
  ro,
  es,
  ru,
];

export interface SfxInfo {
  /** Length of the file, ms. */
  duration: number;
  /** Where the sound "lands" (its peak), ms from the start: whooshes peak on the cut. */
  peak: number;
  volume: number;
  label: Label;
}

export const SFX: Record<SfxKind, SfxInfo> = {
  whoosh: {
    duration: 900,
    peak: 400,
    volume: 0.55,
    label: l('Whoosh', 'Вжух', 'Świst', 'Fâșâit', 'Zumbido', 'Вжух'),
  },
  hit: {
    duration: 700,
    peak: 0,
    volume: 0.75,
    label: l('Hit', 'Удар', 'Uderzenie', 'Lovitură', 'Golpe', 'Удар'),
  },
  boom: {
    duration: 1600,
    peak: 0,
    volume: 0.8,
    label: l('Boom', 'Бум', 'Bum', 'Bubuitură', 'Bum', 'Бум'),
  },
  tick: {
    duration: 70,
    peak: 0,
    volume: 0.35,
    label: l('Tick', 'Тік', 'Tyknięcie', 'Tic', 'Tic', 'Тик'),
  },
  click: {
    duration: 100,
    peak: 0,
    volume: 0.55,
    label: l('Click', 'Клік', 'Klik', 'Clic', 'Clic', 'Клик'),
  },
  riser: {
    duration: 2000,
    peak: 2000,
    volume: 0.45,
    label: l('Riser', 'Райзер', 'Narastanie', 'Riser', 'Subida', 'Райзер'),
  },
  pop: {
    duration: 220,
    peak: 30,
    volume: 0.5,
    label: l('Bubble pop', 'Бульк', 'Bulgot', 'Bulbuc', 'Burbuja', 'Бульк'),
  },
  typewriter: {
    duration: 5000,
    peak: 0,
    volume: 0.4,
    label: l(
      'Typewriter',
      'Друкарська машинка',
      'Maszyna do pisania',
      'Mașină de scris',
      'Máquina de escribir',
      'Печатная машинка',
    ),
  },
  glitch: {
    duration: 500,
    peak: 0,
    volume: 0.4,
    label: l('Glitch', 'Глітч', 'Glitch', 'Glitch', 'Glitch', 'Глитч'),
  },
  shimmer: {
    duration: 1400,
    peak: 80,
    volume: 0.4,
    label: l('Shimmer', 'Мерехтіння', 'Migotanie', 'Sclipire', 'Destello', 'Мерцание'),
  },
};

/** Path of the shipped file (served by the app; the render server synthesises it). */
export const sfxSrc = (kind: SfxKind) => `/sfx/${kind}.wav`;

/** The kind of a shipped effect path, or null. */
export function sfxKindOf(src: string): SfxKind | null {
  const m = /^\/sfx\/([a-z]+)\.wav$/.exec(src.split(/[?#]/)[0]);
  return m && (SFX_KINDS as readonly string[]).includes(m[1]) ? (m[1] as SfxKind) : null;
}

// ---------------------------------------------------------------- DSP helpers

export function lcg(seed: number) {
  let s = seed >>> 0 || 1;
  return () => (s = (s * 1664525 + 1013904223) >>> 0) / 4294967296;
}

/** State-variable filter (Chamberlin): low, band and high outputs, cutoff per sample. */
export function svf() {
  let low = 0;
  let band = 0;
  return (x: number, cutoff: number, q: number, rate: number) => {
    const f = 2 * Math.sin((Math.PI * Math.min(cutoff, rate / 6)) / rate);
    low += f * band;
    const high = x - low - q * band;
    band += f * high;
    return { low, band, high };
  };
}

export function normalize(buf: Float32Array, peak = 0.9): Float32Array {
  let max = 0;
  for (const v of buf) max = Math.max(max, Math.abs(v));
  if (max > 0) for (let i = 0; i < buf.length; i++) buf[i] = (buf[i] / max) * peak;
  return buf;
}

/** Short fades at both ends (no clicks). */
function edges(buf: Float32Array, rate: number, ms = 3) {
  const n = Math.min(buf.length >> 1, Math.round((rate * ms) / 1000));
  for (let i = 0; i < n; i++) {
    buf[i] *= i / n;
    buf[buf.length - 1 - i] *= i / n;
  }
  return buf;
}

// ---------------------------------------------------------------- the effects

export function synthSfx(kind: SfxKind, rate = SFX_RATE): Float32Array {
  const info = SFX[kind];
  const n = Math.round((rate * info.duration) / 1000);
  const out = new Float32Array(n);
  const rnd = lcg(SFX_KINDS.indexOf(kind) * 7919 + 13);
  const noise = () => rnd() * 2 - 1;
  const T = Math.PI * 2;
  switch (kind) {
    case 'whoosh': {
      const f = svf();
      const peak = info.peak / 1000;
      for (let i = 0; i < n; i++) {
        const t = i / rate;
        const k = t < peak ? t / peak : 1 - (t - peak) / (info.duration / 1000 - peak);
        const env = Math.pow(Math.max(0, k), t < peak ? 2.2 : 1.6);
        const cutoff = 250 + 3200 * Math.pow(Math.max(0, k), 1.5);
        out[i] = f(noise(), cutoff, 0.9, rate).band * env;
      }
      break;
    }
    case 'hit': {
      let ph = 0;
      let ph2 = 0;
      const hp = svf();
      for (let i = 0; i < n; i++) {
        const t = i / rate;
        const freq = 42 + 90 * Math.exp(-t * 28);
        ph += (T * freq) / rate;
        ph2 += (T * 180 * (1 + Math.exp(-t * 40))) / rate;
        const body = Math.sin(ph) * Math.exp(-t * 6);
        const knock = Math.sin(ph2) * Math.exp(-t * 30) * 0.5;
        const snap = hp(noise(), 2500, 0.7, rate).high * Math.exp(-t * 60) * 0.6;
        out[i] = Math.tanh((body + knock + snap) * 1.6);
      }
      break;
    }
    case 'boom': {
      let ph = 0;
      const lp = svf();
      for (let i = 0; i < n; i++) {
        const t = i / rate;
        ph += (T * (30 + 40 * Math.exp(-t * 6))) / rate;
        const sub = Math.sin(ph) * Math.exp(-t * 2.4);
        const rumble = lp(noise(), 220, 0.6, rate).low * Math.exp(-t * 3) * 1.8;
        out[i] = Math.tanh((sub + rumble) * 1.4);
      }
      break;
    }
    case 'tick': {
      const hp = svf();
      for (let i = 0; i < n; i++) {
        const t = i / rate;
        out[i] =
          Math.sin(T * 2400 * t) * Math.exp(-t * 260) +
          hp(noise(), 4000, 0.6, rate).high * Math.exp(-t * 500) * 0.5;
      }
      break;
    }
    case 'click': {
      for (let i = 0; i < n; i++) {
        const t = i / rate;
        const a = Math.sin(T * 1700 * t) * Math.exp(-t * 300);
        const t2 = Math.max(0, t - 0.028);
        const b = t > 0.028 ? Math.sin(T * 2900 * t2) * Math.exp(-t2 * 380) * 0.7 : 0;
        out[i] = a + b;
      }
      break;
    }
    case 'riser': {
      const f = svf();
      let ph = 0;
      const lp = svf();
      const D = info.duration / 1000;
      for (let i = 0; i < n; i++) {
        const t = i / rate;
        const k = t / D;
        const env = Math.pow(k, 2.2);
        const cutoff = 200 + 7000 * Math.pow(k, 2);
        const air = f(noise(), cutoff, 0.5, rate).band;
        ph += (T * (110 * Math.pow(2, 3 * k))) / rate;
        const saw = ((ph / T) % 1) * 2 - 1;
        const tone = lp(saw, 400 + 4000 * k, 0.8, rate).low * 0.6;
        out[i] = (air + tone) * env;
      }
      break;
    }
    case 'pop': {
      let ph = 0;
      for (let i = 0; i < n; i++) {
        const t = i / rate;
        // A bubble: pitch rising fast while it decays, with a small second bubble
        const freq = 380 * Math.pow(4, Math.min(1, t / 0.06));
        ph += (T * freq) / rate;
        const t2 = t - 0.09;
        const second =
          t2 > 0
            ? Math.sin(T * (600 * Math.pow(3, Math.min(1, t2 / 0.04))) * t2) *
              Math.exp(-t2 * 70) *
              0.4
            : 0;
        out[i] = Math.sin(ph) * Math.exp(-t * 45) + second;
      }
      break;
    }
    case 'typewriter': {
      const bp = svf();
      let at = 0.02;
      const hits: Array<{ at: number; level: number; tone: number }> = [];
      while (at < info.duration / 1000 - 0.1) {
        hits.push({ at, level: 0.6 + rnd() * 0.4, tone: 300 + rnd() * 200 });
        at += 0.075 + rnd() * 0.07 + (rnd() < 0.08 ? 0.18 : 0);
      }
      let h = 0;
      for (let i = 0; i < n; i++) {
        const t = i / rate;
        while (h < hits.length - 1 && t >= hits[h + 1].at) h++;
        const hit = hits[h];
        const dt = t - hit.at;
        if (dt < 0) continue;
        const clack = bp(noise(), 3200, 0.5, rate).band * Math.exp(-dt * 260) * 1.6;
        const body = Math.sin(T * hit.tone * dt) * Math.exp(-dt * 90) * 0.5;
        out[i] = (clack + body) * hit.level;
      }
      break;
    }
    case 'glitch': {
      let seg = 0;
      let segEnd = 0;
      let freq = 200;
      let mode = 0;
      let held = 0;
      for (let i = 0; i < n; i++) {
        if (i >= segEnd) {
          seg++;
          segEnd = i + Math.round(rate * (0.012 + rnd() * 0.05));
          freq = 80 + rnd() * 1800;
          mode = Math.floor(rnd() * 3);
        }
        const t = i / rate;
        let v = 0;
        if (mode === 0) v = Math.sign(Math.sin(T * freq * t)) * 0.6;
        else if (mode === 1) v = i % 8 === 0 ? (held = noise()) : held;
        else v = 0;
        out[i] = v * (seg % 5 === 4 ? 0 : 1) * (1 - (t / (info.duration / 1000)) * 0.6);
      }
      break;
    }
    case 'shimmer': {
      const partials = Array.from({ length: 24 }, () => ({
        at: rnd() * 0.9,
        f: 2000 + rnd() * 5000,
        d: 8 + rnd() * 10,
        a: 0.3 + rnd() * 0.7,
      }));
      for (let i = 0; i < n; i++) {
        const t = i / rate;
        let v = 0;
        for (const p of partials) {
          const dt = t - p.at;
          if (dt >= 0) v += Math.sin(T * p.f * dt) * Math.exp(-dt * p.d) * p.a;
        }
        out[i] = v * Math.min(1, t * 30);
      }
      break;
    }
  }
  return normalize(edges(out, rate, kind === 'tick' || kind === 'click' ? 0.5 : 3), 0.89);
}

/** 16-bit PCM WAV (mono or interleaved stereo). */
export function encodeWav(samples: Float32Array, rate: number, channels = 1): Uint8Array {
  const bytes = samples.length * 2;
  const buf = new ArrayBuffer(44 + bytes);
  const v = new DataView(buf);
  const str = (o: number, s: string) => {
    for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i));
  };
  str(0, 'RIFF');
  v.setUint32(4, 36 + bytes, true);
  str(8, 'WAVE');
  str(12, 'fmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, channels, true);
  v.setUint32(24, rate, true);
  v.setUint32(28, rate * channels * 2, true);
  v.setUint16(32, channels * 2, true);
  v.setUint16(34, 16, true);
  str(36, 'data');
  v.setUint32(40, bytes, true);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    v.setInt16(44 + i * 2, Math.round(s * 32767), true);
  }
  return new Uint8Array(buf);
}

/** The WAV file of a shipped effect. */
export function sfxWav(kind: SfxKind): Uint8Array {
  return encodeWav(synthSfx(kind), SFX_RATE);
}
